import { useMemo, useState } from 'react';
import { scrollPageTo, scrollTop } from '@/lib/scroll';
import { useNavigate, useParams } from 'react-router';
import { BookOpenText, CheckCircle2, ChevronLeft, HeartCrack, Moon, RotateCcw, Settings, ShieldCheck, SkipForward, Sun, Undo2 } from 'lucide-react';
import data from '@/data/adhkar.json';
import { dayKeyIn } from '@shared/prayer';
import { useProgress, useSettings } from '@/lib/store';
import { Card, IconButton, LargeTitle, Ring, Sheet, haptic } from '@/components/ui';
import { AdhkarArabic, AdhkarDisplayButton } from './AdhkarText';
import { useDhikrTotals } from './DhikrCounter';
import { SkyScene } from '@/components/islamic/Scene';
import { TasbihIcon } from '@/components/islamic/Ornaments';
import { HadithList, hadithIdsFor } from './Virtues';

/** title: one-line summary shown above the Arabic. quran: verse keys for Qurʾānic items (official text + tajweed). */
interface Dhikr { id: number; when: 'both' | 'morning' | 'evening'; title: string; ar: string; tr: string; en: string; count: number; countLabel: string; virtue: string; source: string; note?: string; quran?: string[]; taawwudh?: boolean }
const ITEMS = (data as { items: Dhikr[]; credit: string }).items;
export const ADHKAR_CREDIT = (data as { credit: string }).credit;
export const adhkarFor = (when: 'morning' | 'evening') => ITEMS.filter((d) => d.when === 'both' || d.when === when);

function useToday() {
  const tz = useSettings((s) => s.settings.prayer.tz);
  return dayKeyIn(tz);
}

export function useAdhkarProgress(when: 'morning' | 'evening') {
  const dayKey = useToday();
  const part = useProgress((s) => s.adhkar[dayKey]?.[when]);
  const items = adhkarFor(when);
  const done = items.filter((d) => (part?.counts[d.id] ?? 0) >= d.count).length;
  return { done, total: items.length, complete: !!part?.doneAt, dayKey };
}

function DhikrCard() {
  const t = useDhikrTotals();
  return (
    <Card to="/adhkar/dhikr" className="flex items-center gap-4 p-5">
      <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-accent/15 text-accent"><TasbihIcon size={30} /></span>
      <div className="min-w-0 flex-1">
        <div className="text-[18px] font-bold">Dhikr counter</div>
        <div className="font-kufi text-[16px] text-gold">الذكر</div>
        <div className="text-[13px] text-muted">Istighfār, tasbīḥ and more · {t.today.toLocaleString('en-GB')} today · {t.week.toLocaleString('en-GB')} this week</div>
      </div>
    </Card>
  );
}

export function AdhkarHome() {
  const morning = useAdhkarProgress('morning');
  const evening = useAdhkarProgress('evening');
  const hour = new Date().getHours();
  const suggest = hour >= 3 && hour < 14 ? 'morning' : 'evening';
  const tile = (when: 'morning' | 'evening', p: typeof morning) => (
    <Card to={`/adhkar/${when}`} className={`relative overflow-hidden !p-0 ${suggest === when ? 'ring-2 ring-gold/60' : ''}`}>
      <SkyScene phase={when === 'morning' ? 'dawn' : 'dusk'} height={150} caravan={when === 'evening'} />
      <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/30 to-transparent" />
      <div className="absolute inset-0 flex items-center gap-4 p-5 text-white">
        <div className="rounded-full bg-black/25 p-1 backdrop-blur-sm">
          <Ring value={p.done / p.total} size={64} stroke={7}>{p.complete ? <CheckCircle2 className="text-[#9ff0c9]" size={26} /> : when === 'morning' ? <Sun size={24} className="text-[#f3d27a]" /> : <Moon size={22} className="text-[#f3d27a]" />}</Ring>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[20px] font-bold drop-shadow">{when === 'morning' ? 'Morning adhkar' : 'Evening adhkar'}</div>
          <div className="font-kufi text-[18px] text-[#f3d27a]">{when === 'morning' ? 'أذكار الصباح' : 'أذكار المساء'}</div>
          <div className="text-[13px] text-white/85">{p.complete ? 'Completed today — mā shāʾ Allāh' : `${p.done} of ${p.total} done`}{suggest === when && !p.complete ? ' · now' : ''}</div>
        </div>
      </div>
    </Card>
  );
  return (
    <div className="page-pad">
      <LargeTitle title="Adhkar" ar="الأذكار" subtitle="Remembrance" right={<IconButton label="Settings" to="/settings/notifications"><Settings size={18} /></IconButton>} />
      <div className="space-y-3 px-4">
        {tile('morning', morning)}
        {tile('evening', evening)}
        <DhikrCard />
        <Card to="/duas" className="flex items-center gap-4 p-5">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-accent/15 text-accent"><BookOpenText size={26} /></span>
          <div className="min-w-0 flex-1">
            <div className="text-[18px] font-bold">Duʿāʾ</div>
            <div className="font-kufi text-[16px] text-gold">حصن المسلم</div>
            <div className="text-[13px] text-muted">Ḥiṣn al-Muslim — duʿāʾ for every occasion, including Istikhārah</div>
          </div>
        </Card>
        <Card to="/adhkar/sins" className="flex items-center gap-4 p-5">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-[var(--win-crit)]/12 text-[var(--win-crit)]"><HeartCrack size={26} /></span>
          <div className="min-w-0 flex-1">
            <div className="text-[18px] font-bold">Effects of sins</div>
            <div className="font-kufi text-[16px] text-gold">آثار الذنوب</div>
            <div className="text-[13px] text-muted">What sins do to the heart, provision and duʿāʾ — and the way back through tawbah</div>
          </div>
        </Card>
        <Card to="/adhkar/ruqyah" className="flex items-center gap-4 p-5">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-gold/15 text-gold"><ShieldCheck size={26} /></span>
          <div className="min-w-0 flex-1">
            <div className="text-[18px] font-bold">Ruqyah</div>
            <div className="font-kufi text-[16px] text-gold">الرقية الشرعية</div>
            <div className="text-[13px] text-muted">Healing and protection — Qurʾān, the Prophet’s ﷺ duʿāʾ, and how to do it</div>
          </div>
        </Card>
        <p className="px-2 pt-2 text-[13px] text-muted">Morning adhkar are read after Fajr until sunrise; evening adhkar after ʿAsr until Maghrib. Reminders can be set in Settings → Notifications. Source: {ADHKAR_CREDIT}.</p>
      </div>
    </div>
  );
}

export function AdhkarList() {
  const { when = 'morning' } = useParams() as { when?: 'morning' | 'evening' };
  const nav = useNavigate();
  const dayKey = useToday();
  const items = useMemo(() => adhkarFor(when), [when]);
  const part = useProgress((s) => s.adhkar[dayKey]?.[when]);
  const setCount = useProgress((s) => s.setDhikrCount);
  const reset = useProgress((s) => s.resetAdhkar);
  const skipDhikr = useProgress((s) => s.skipDhikr);
  const finishAdhkar = useProgress((s) => s.finishAdhkar);
  const addSession = useProgress((s) => s.addSession);
  const [started] = useState(() => Date.now());
  const [showTr, setShowTr] = useState(true);
  const [info, setInfo] = useState<Dhikr | null>(null);
  const counts = part?.counts ?? {};
  const done = items.filter((d) => (counts[d.id] ?? 0) >= d.count).length;

  const skipped = new Set(part?.skipped ?? []);
  const finished = !!part?.doneAt;
  const settled = (x: Dhikr, c = counts[x.id] ?? 0, skip = skipped.has(x.id)) => c >= x.count || skip;
  const logSession = () => addSession({ kind: 'adhkar', start: started, seconds: Math.round((Date.now() - started) / 1000) });

  /* Bring the next dhikr into view. A plain page scroll — iOS's smooth
     scrollIntoView can shift the visual viewport and drag the fixed tab bar up
     into the middle of the screen. */
  const scrollToNext = (d: Dhikr) => {
    const next = items.slice(items.indexOf(d) + 1).find((x) => !settled(x));
    const el = next && document.getElementById(`d-${next.id}`);
    if (!el) return;
    const r = el.getBoundingClientRect();
    scrollPageTo(scrollTop() + r.top - Math.max(80, (window.innerHeight - r.height) / 2), true);
  };

  const tap = (d: Dhikr) => {
    const c = counts[d.id] ?? 0;
    if (c >= d.count) return;
    const next = c + 1;
    haptic(next === d.count ? 30 : 8);
    const allDone = items.every((x) => (x.id === d.id ? settled(x, next) : settled(x)));
    setCount(dayKey, when, d.id, next, allDone);
    if (next === d.count) {
      if (allDone && !finished) logSession();
      setTimeout(() => scrollToNext(d), 250);
    }
  };

  /* Skipping: you don't always have time for every dhikr — the set still counts once the rest are done. */
  const skip = (d: Dhikr, on: boolean) => {
    haptic(on ? 12 : 6);
    const allDone = items.every((x) => (x.id === d.id ? settled(x, undefined, on) : settled(x)));
    skipDhikr(dayKey, when, d.id, on, allDone);
    if (on) { if (allDone && !finished) logSession(); setTimeout(() => scrollToNext(d), 150); }
  };
  const finish = () => {
    haptic(30);
    if (!finished) logSession();
    finishAdhkar(dayKey, when);
    scrollPageTo(0, true);
  };

  return (
    <div className="page-pad !pt-0">
      <header className="glass sticky top-0 z-30 flex items-center gap-2 px-3 pb-2" style={{ paddingTop: 'calc(var(--safe-top) + 8px)' }}>
        <IconButton label="Back" onClick={() => nav('/adhkar')}><ChevronLeft size={20} /></IconButton>
        <div className="flex-1 text-center">
          <div className="text-[16px] font-bold">{when === 'morning' ? 'Morning adhkar' : 'Evening adhkar'}</div>
          <div className="text-[12px] text-muted">{finished ? 'Done ✓ · ' : ''}{done} / {items.length}{skipped.size ? ` · ${skipped.size} skipped` : ''}</div>
        </div>
        <IconButton label="Toggle translation" onClick={() => setShowTr(!showTr)}><span className="text-[13px] font-bold">EN</span></IconButton>
        <AdhkarDisplayButton showTr={showTr} setShowTr={setShowTr} />
        <IconButton label="Reset" onClick={() => { if (confirm('Reset today\'s count?')) reset(dayKey, when); }}><RotateCcw size={17} /></IconButton>
      </header>
      <div className="h-1 bg-fill"><div className="h-full bg-accent transition-all" style={{ width: `${(done / items.length) * 100}%` }} /></div>

      <div className="mx-auto max-w-[720px] space-y-3 px-4 pt-4">
        {items.map((d) => {
          const c = counts[d.id] ?? 0;
          const complete = c >= d.count;
          const isSkipped = !complete && skipped.has(d.id);
          return (
            <div key={d.id} id={`d-${d.id}`} role="button" tabIndex={0} aria-label={`Count ${d.countLabel || d.count}`} onClick={() => { if (!isSkipped) tap(d); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!isSkipped) tap(d); } }}
              className={`press block w-full cursor-pointer rounded-[22px] p-5 text-left transition-colors ${complete ? 'bg-accent/10 opacity-70' : isSkipped ? 'bg-surface opacity-45' : 'bg-surface'}`}>
              <h3 className="mb-2 text-[13px] font-semibold leading-snug text-gold">{d.title}</h3>
              <AdhkarArabic ar={d.ar} quran={d.quran} taawwudh={d.taawwudh} />
              {showTr && <>
                <p className="mt-3 text-[14px] italic leading-relaxed text-muted">{d.tr}</p>
                <p className="mt-2 text-[15px] leading-relaxed">{d.en}</p>
              </>}
              <div className="mt-4 flex items-center justify-between gap-3">
                <button type="button" onClick={(e) => { e.stopPropagation(); haptic(); setInfo(d); }}
                  className="press flex min-w-0 items-center gap-1.5 rounded-full bg-gold/15 px-3 py-1.5 text-[13px] font-semibold text-gold">
                  <BookOpenText size={15} className="shrink-0" /> <span className="truncate">{d.virtue || hadithIdsFor('adhkar', d.id).length ? 'Virtue & source' : 'Source'}</span>
                </button>
                <span className="flex shrink-0 items-center gap-2">
                  {!complete && (
                    <button type="button" onClick={(e) => { e.stopPropagation(); skip(d, !isSkipped); }}
                      className="press flex items-center gap-1 rounded-full bg-fill px-3 py-1.5 text-[13px] font-semibold text-muted">
                      {isSkipped ? <><Undo2 size={14} /> Undo skip</> : <><SkipForward size={14} /> Skip</>}
                    </button>
                  )}
                  <span className={`flex h-12 min-w-12 items-center justify-center rounded-full px-3 text-[17px] font-bold tabular-nums ${complete ? 'bg-accent text-accent-ink' : 'bg-fill'}`}>
                    {complete ? <CheckCircle2 size={22} /> : isSkipped ? 'Skipped' : `${c}/${d.count}`}
                  </span>
                </span>
              </div>
            </div>
          );
        })}
        <div className="pb-4 pt-2">
          {finished ? (
            <p className="rounded-2xl bg-accent/10 px-4 py-3 text-center text-[15px] font-semibold text-accent">
              {when === 'morning' ? 'Morning' : 'Evening'} adhkar done — may Allah accept it{skipped.size ? ` (${skipped.size} skipped)` : ''}.
            </p>
          ) : (
            <button type="button" onClick={finish} className="press w-full rounded-2xl bg-accent py-3.5 text-[16px] font-semibold text-accent-ink">
              I’ve finished my {when} adhkar
            </button>
          )}
        </div>
        <Sheet open={!!info} onClose={() => setInfo(null)} title={info && (info.virtue || hadithIdsFor('adhkar', info.id).length) ? 'Virtue' : 'Source'}>
          {info && (
            <div className="space-y-4 px-5 pb-4">
              <h3 className="text-[15px] font-semibold text-gold">{info.title}</h3>
              <AdhkarArabic ar={info.ar} quran={info.quran} taawwudh={info.taawwudh} base={20} />
              <p className="text-[15px] leading-relaxed">{info.en}</p>
              {info.virtue && (
                <div className="rounded-2xl bg-gold/10 p-4">
                  <div className="pb-1 text-[12px] font-semibold uppercase tracking-wide text-gold">Virtue · فضل</div>
                  <p className="text-[15px] leading-relaxed">{info.virtue}</p>
                </div>
              )}
              <HadithList ids={hadithIdsFor('adhkar', info.id)} />
              {info.note && <p className="rounded-2xl bg-fill p-3 text-[13px] leading-relaxed text-muted">{info.note}</p>}
              <div>
                <div className="pb-1 text-[12px] font-semibold uppercase tracking-wide text-muted">Hadith & reference</div>
                <p className="text-[14px] leading-relaxed text-muted">{info.source}</p>
              </div>
              <p className="text-[13px] text-muted">Read {info.countLabel ? info.countLabel.toLowerCase() : `${info.count}×`}.</p>
            </div>
          )}
        </Sheet>
        {done === items.length && (
          <div className="py-8 text-center">
            <CheckCircle2 size={44} className="mx-auto text-accent" />
            <div className="mt-2 text-[19px] font-bold">Completed — may Allah accept</div>
            <div className="font-arabic text-[18px] text-gold">تقبّل الله</div>
          </div>
        )}
      </div>
    </div>
  );
}
